import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
  RouterProvider,
  RoutesProvider,
  ScrollRestoration,
  useLocation,
} from '@faceup/router'
import { getValidBasename, languageEnumToBasename } from '@faceup/utils'
import { lazy, Suspense, useMemo } from 'react'
import { FullScreenLoader } from '../../Components/FullScreenLoader'
import NavigationChange from '../../Components/NavigationChange'
import PointerEventsMember from '../../Components/PointerEventsMember'
import { RouterFullScreenLoader } from '../../Components/RouterFullScreenLoader'
import { useRoutes } from '../../hooks/useRoutes'
import { useSavedReportsContext } from '../../hooks/useSavedReportsContext'
import { useAccessKey } from '../AccessKeyProvider'
import { AppErrorBoundary } from '../AppErrorBoundary'
import { useAppLock } from '../AppLockProvider'
import { useClient } from '../ClientProvider'
import { getInitialLanguage } from '../initialLanguage'
import { ReportSourceWrapper, reportLayoutHandle } from './components/ReportSourceWrapper'
import { SurveyWrapper } from './components/SurveyWrapper'

const SearchInstitution = lazy(() => import('../../Pages/SearchInstitution/SearchInstitution'))
const EmailVerification = lazy(() => import('../../Components/EmailVerification'))
const Offline = lazy(() => import('../../Pages/Offline/Offline'))
const Biometrics = lazy(() => import('../../Pages/Authentication/BiometricsPage'))
const LandingPage = lazy(() => import('../../Pages/Authentication/LandingPage'))
const CreateReport = lazy(() => import('../../Pages/CreateReport/CreateReport'))
const ReportSent = lazy(() => import('../../Pages/ReportSent'))
const CheckReport = lazy(() => import('../../Pages/CheckReport'))
const CheckReportList = lazy(() =>
  import('../../Pages/CheckReportList/CheckReportList').then(m => ({ default: m.CheckReportList }))
)
const Report = lazy(() => import('../../Pages/Report/Report'))
const PageView = lazy(() => import('../../Pages/Page/PageView'))
const DefaultPageRedirect = lazy(() => import('../../Pages/Page/DefaultPageRedirect'))
const SurveyView = lazy(() => import('../../Pages/Survey/SurveyView'))
const SurveySent = lazy(() => import('../../Pages/SurveySent/SurveySent'))
const DisclosureView = lazy(() =>
  import('../../Pages/Disclosure/DisclosureView').then(m => ({ default: m.DisclosureView }))
)

const branchFallback = <FullScreenLoader />

const surveyRoutes: RouteObject[] = [
  {
    path: ':language/q/:shortId',
    element: (
      <Suspense fallback={branchFallback}>
        <SurveyWrapper />
      </Suspense>
    ),
    children: [
      { index: true, element: <SurveyView /> },
      { path: 'sent', element: <SurveySent /> },
    ],
  },
]

const disclosureRoutes: RouteObject[] = [
  {
    path: ':language/d/:shortId',
    element: (
      <Suspense fallback={branchFallback}>
        <DisclosureView />
      </Suspense>
    ),
  },
]

/**
 * `check` route guard: app users who already have saved reports see the list
 * of their reports (CheckReportList); everyone else - web always, plus app
 * users with no saved reports - gets the CheckReport PIN form. `check/form`
 * always renders the PIN form so the list's "check another report" button has
 * a target.
 */
const CheckReportRoute = () => {
  const { client } = useClient()
  const { hasSomeSavedReports } = useSavedReportsContext()

  if (client === 'app' && hasSomeSavedReports) {
    return <CheckReportList />
  }
  return <CheckReport />
}

const reportRoutes: RouteObject[] = [
  {
    path: ':language/c/:shortId',
    element: (
      <Suspense fallback={branchFallback}>
        <ReportSourceWrapper />
      </Suspense>
    ),
    children: [
      { index: true, element: <Navigate to={routes => routes.page()} replace /> },
      {
        path: 'create',
        element: <CreateReport />,
        handle: reportLayoutHandle({ variant: 'simple', topAligned: true }),
      },
      { path: 'sent', element: <ReportSent />, handle: reportLayoutHandle({ variant: 'simple' }) },
      {
        path: 'check',
        element: <CheckReportRoute />,
        handle: reportLayoutHandle({ variant: 'simple' }),
      },
      {
        path: 'check/form',
        element: <CheckReport />,
        handle: reportLayoutHandle({ variant: 'simple' }),
      },
      { path: 'report', element: <Report />, handle: reportLayoutHandle({ variant: 'simple' }) },
      { path: 'page/:id', element: <PageView /> },
      { path: 'page', element: <DefaultPageRedirect /> },
    ],
  },
  {
    path: ':language/s/:shortId',
    element: <Navigate to={routes => routes.page()} replace />,
  },
]

/**
 * Home index guard: app users with an unfinished setup go to biometrics, app
 * users with a remembered institution skip search and go straight to its page,
 * everyone else searches. On the web isSetUp() is always true and there is no
 * remembered access key, so it is just SearchInstitution.
 */
const HomeRedirect = () => {
  const { isSetUp } = useAppLock()
  const { accessKey } = useAccessKey()

  if (!isSetUp()) {
    return <Navigate to={routes => routes.settingsBiometrics()} replace />
  }
  if (accessKey?.shortLink) {
    return <Navigate to={routes => routes.page({ shortId: accessKey.shortLink })} replace />
  }
  return <SearchInstitution />
}

const homeRoutes: RouteObject[] = [
  {
    path: ':language',
    element: (
      <Suspense fallback={branchFallback}>
        <Outlet />
      </Suspense>
    ),
    children: [
      { index: true, element: <HomeRedirect /> },
      { path: 'token/:id', element: <EmailVerification /> },
      { path: 'offline', element: <Offline /> },
      { path: 'settings/biometrics', element: <Biometrics /> },
    ],
  },
]

/**
 * Catch-all for paths without a language prefix (shared links / printed QR codes
 * point at /c/:shortId). Prepend the resolved language and keep the rest of the
 * path. If the URL already has a valid language basename but matched nothing, fall
 * back to that language's home so we never loop on the splat.
 */
const LanguageRedirect = () => {
  const location = useLocation()
  const validBasename = getValidBasename()

  if (validBasename !== '/') {
    return <Navigate to={() => validBasename} replace />
  }

  const basename = languageEnumToBasename(getInitialLanguage())
  const suffix = location.pathname === '/' ? '' : location.pathname
  return <Navigate to={() => `/${basename}${suffix}${location.search}`} replace />
}

const RootLayout = () => {
  const routeLinks = useRoutes()
  return (
    <RoutesProvider routes={routeLinks}>
      <NavigationChange />
      <PointerEventsMember>
        <ScrollRestoration />
        <Outlet />
      </PointerEventsMember>
    </RoutesProvider>
  )
}

export const AppRouter = () => {
  // Router must be created only once - recreating it on every render causes stale
  // navigate references (URL changes but UI doesn't update).
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/',
          element: <RootLayout />,
          errorElement: <AppErrorBoundary />,
          children: [
            ...surveyRoutes,
            ...reportRoutes,
            ...disclosureRoutes,
            ...homeRoutes,
            // index = bare "/" (splat "*" does not match an empty remainder)
            { index: true, element: <LanguageRedirect /> },
            { path: '*', element: <LanguageRedirect /> },
          ],
        },
        { path: '/*', Component: RouterFullScreenLoader },
      ]),
    []
  )

  const { isAuthenticated } = useAppLock()

  return (
    <>
      {/* App PIN lock: keep the router mounted (preserves input state) but hide it
          behind the landing/lock screen until authenticated. No-op on the web. */}
      {!isAuthenticated && (
        <Suspense fallback={<FullScreenLoader />}>
          <LandingPage />
        </Suspense>
      )}
      <div className={isAuthenticated ? '' : 'hidden absolute top-0 max-h-screen overflow-hidden'}>
        <RouterProvider router={router} />
      </div>
    </>
  )
}
